import type { CSSProperties } from "react";

export const canonicalAssetCatalog = {
  "LC-ASSET-IDENTITY-AUTHORITY-MASK-LC-ID-LOCKED-WORDMARK-CURRENTCOLOR": {
    path: "identity/authority-mask/LC-ID-LOCKED-WORDMARK-currentColor.svg",
    kind: "identity",
    fictional: false,
  },
  "LC-ASSET-IDENTITY-VARIANTS-LC-IDENTITY-AUTHORITY-01-INK-GOLD": {
    path: "identity/variants/LC-IDENTITY-AUTHORITY-01-ink-gold.svg",
    kind: "identity",
    fictional: false,
  },
  "LC-ASSET-IDENTITY-VARIANTS-LC-IDENTITY-AUTHORITY-02-PAPER-GOLD": {
    path: "identity/variants/LC-IDENTITY-AUTHORITY-02-paper-gold.svg",
    kind: "identity",
    fictional: false,
  },
  "LC-ASSET-MATERIALS-MATTE-PAPER": {
    path: "materials/matte-paper.png",
    kind: "material",
    fictional: false,
  },
  "LC-ASSET-DECORATIVE-COFFEE-CUP": {
    path: "decorative/coffee-cup.png",
    kind: "decorative",
    fictional: false,
  },
  "LC-ASSET-DECORATIVE-EUCALYPTUS-HEADER": {
    path: "decorative/eucalyptus-header.png",
    kind: "decorative",
    fictional: false,
  },
  "LC-ASSET-DECORATIVE-EUCALYPTUS-FOOTER": {
    path: "decorative/eucalyptus-footer.png",
    kind: "decorative",
    fictional: false,
  },
  "LC-ASSET-DECORATIVE-GINGHAM-CORNER": {
    path: "decorative/gingham-corner.png",
    kind: "decorative",
    fictional: false,
  },
  "LC-ASSET-SCENES-EXACT-HERO-OPEN-BOOK": {
    path: "scenes/exact/LC-SCENE-HERO-OPEN-BOOK.png",
    kind: "fictional-scene-fixture",
    fictional: true,
  },
  "LC-ASSET-BOOKS-FIXTURES-NARRATIVE-MY-STORY": {
    path: "books/fixtures/narrative-my-story.png",
    kind: "fictional-book-fixture",
    fictional: true,
  },
  "LC-ASSET-BOOKS-FIXTURES-NARRATIVE-OUR-STORY": {
    path: "books/fixtures/narrative-our-story.png",
    kind: "fictional-book-fixture",
    fictional: true,
  },
  "LC-ASSET-BOOKS-FIXTURES-PICTURE-LITTLE-MOMENTS": {
    path: "books/fixtures/picture-little-moments.png",
    kind: "fictional-book-fixture",
    fictional: true,
  },
  "LC-ASSET-BOOKS-FIXTURES-RECIPE-FAMILY-RECIPES": {
    path: "books/fixtures/recipe-family-recipes.png",
    kind: "fictional-book-fixture",
    fictional: true,
  },
  "LC-ASSET-BOOKS-FIXTURES-NARRATIVE-MY-STORY-CONTEXT": {
    path: "books/fixtures/narrative-my-story-context.png",
    kind: "fictional-book-fixture",
    fictional: true,
  },
  "LC-ASSET-BOOKS-FIXTURES-NARRATIVE-OUR-STORY-CONTEXT": {
    path: "books/fixtures/narrative-our-story-context.png",
    kind: "fictional-book-fixture",
    fictional: true,
  },
  "LC-ASSET-BOOKS-FIXTURES-PICTURE-LITTLE-MOMENTS-CONTEXT": {
    path: "books/fixtures/picture-little-moments-context.png",
    kind: "fictional-book-fixture",
    fictional: true,
  },
  "LC-ASSET-BOOKS-FIXTURES-RECIPE-FAMILY-RECIPES-CONTEXT": {
    path: "books/fixtures/recipe-family-recipes-context.png",
    kind: "fictional-book-fixture",
    fictional: true,
  },
  "LC-ASSET-BOOKS-FIXTURES-SATURDAY-MORNING-PANCAKES-HERO-SPREAD": {
    path: "books/fixtures/saturday-morning-pancakes-hero-spread.png",
    kind: "fictional-book-fixture",
    fictional: true,
  },
  "LC-ASSET-BOOKS-FIXTURES-MORE-THAN-PANCAKES-SPREAD": {
    path: "books/fixtures/more-than-pancakes-spread.png",
    kind: "fictional-book-fixture",
    fictional: true,
  },
  "LC-ASSET-BOOKS-FIXTURES-GRANDMAS-GARDEN-SPREAD": {
    path: "books/fixtures/grandmas-garden-spread.png",
    kind: "fictional-book-fixture",
    fictional: true,
  },
  "LC-ASSET-MEMORY-ARTIFACTS-ARCHIVAL-PHOTO": {
    path: "memory-artifacts/transparent/archival-photo.png",
    kind: "fictional-memory-fixture",
    fictional: true,
  },
  "LC-ASSET-MEMORY-ARTIFACTS-HANDWRITTEN-RECIPE": {
    path: "memory-artifacts/transparent/handwritten-recipe.png",
    kind: "fictional-memory-fixture",
    fictional: true,
  },
  "LC-ASSET-MEMORY-ARTIFACTS-LAKE-POLAROID": {
    path: "memory-artifacts/transparent/lake-polaroid.png",
    kind: "fictional-memory-fixture",
    fictional: true,
  },
  "LC-ASSET-MEMORY-ARTIFACTS-VOICE-CONTROL": {
    path: "memory-artifacts/exact/voice-control-brand.png",
    kind: "fictional-memory-fixture",
    fictional: true,
  },
  "LC-ASSET-MEMORY-ARTIFACTS-VOICE-MEMO-STRIP": {
    path: "memory-artifacts/exact/voice-memo-strip.png",
    kind: "fictional-memory-fixture",
    fictional: true,
  },
  "LC-ASSET-MEMORY-ARTIFACTS-REAL-MOMENTS-NOTE": {
    path: "memory-artifacts/exact/real-moments-note.png",
    kind: "fictional-memory-fixture",
    fictional: true,
  },
  "LC-ASSET-DECORATIVE-STUDIO-BOTANICAL-NOTE": {
    path: "decorative/studio-botanical-note.png",
    kind: "decorative",
    fictional: false,
  },
  "LC-ASSET-DECORATIVE-STUDIO-MOBILE-BOTANICAL-NOTE": {
    path: "decorative/studio-mobile-botanical-note.png",
    kind: "decorative",
    fictional: false,
  },
} as const;

export type CanonicalAssetId = keyof typeof canonicalAssetCatalog;

function normalizeAssetBaseUrl(value: string | undefined) {
  const candidate = value?.trim();
  if (!candidate || candidate.startsWith("//")) return undefined;
  if (candidate.startsWith("/")) return candidate.replace(/\/+$/u, "");
  try {
    const parsed = new URL(candidate);
    if (parsed.protocol !== "https:" && parsed.protocol !== "http:") return undefined;
    return parsed.href.replace(/\/+$/u, "");
  } catch {
    return undefined;
  }
}

export function canonicalAssetBaseUrl() {
  return normalizeAssetBaseUrl(import.meta.env.VITE_CANONICAL_ASSET_BASE_URL as string | undefined);
}

export function resolveCanonicalAssetUrl(
  id: CanonicalAssetId,
  baseUrl: string | undefined = canonicalAssetBaseUrl(),
) {
  const normalizedBase = normalizeAssetBaseUrl(baseUrl);
  if (!normalizedBase) return undefined;
  const encodedPath = canonicalAssetCatalog[id].path
    .split("/")
    .map((segment) => encodeURIComponent(segment))
    .join("/");
  return `${normalizedBase}/${encodedPath}`;
}

export function canonicalAssetSurfaceStyle(id: CanonicalAssetId, baseUrl?: string) {
  const url = resolveCanonicalAssetUrl(id, baseUrl);
  return url ? ({ "--lc-canonical-surface": `url("${url}")` } as CSSProperties) : undefined;
}

const exampleCoverByTemplate: Record<string, CanonicalAssetId> = {
  family: "LC-ASSET-BOOKS-FIXTURES-NARRATIVE-OUR-STORY",
  story: "LC-ASSET-BOOKS-FIXTURES-NARRATIVE-MY-STORY",
  memoir: "LC-ASSET-BOOKS-FIXTURES-NARRATIVE-MY-STORY",
  children: "LC-ASSET-BOOKS-FIXTURES-PICTURE-LITTLE-MOMENTS",
  recipes: "LC-ASSET-BOOKS-FIXTURES-RECIPE-FAMILY-RECIPES",
};

export function canonicalExampleCoverAsset(templateId: string, isExplicitExample: boolean) {
  return isExplicitExample ? exampleCoverByTemplate[templateId] : undefined;
}
